import { z } from "zod"

/**
 * Invoice branding and templates (T467, PRD R2.8-R2.9, docs/specs/invoice-templates-receipts.md).
 * Web only, not a sync entity. Request schemas strict; stored/response schemas lenient (no
 * `.max()`, plain strings) because the server parses every stored row with them.
 * `INVOICE_TEMPLATES` is the one source for the DB CHECK lists (apps/web schema.test.ts).
 */

export const INVOICE_TEMPLATES = ["classic", "modern", "compact"] as const
export const invoiceTemplateSchema = z.enum(INVOICE_TEMPLATES)
export type InvoiceTemplate = z.infer<typeof invoiceTemplateSchema>

/** Logo upload limits (Owner Q8): PNG/JPEG only, re-encoded server side. */
export const LOGO_MIME_TYPES = ["image/png", "image/jpeg"] as const
export const LOGO_MAX_BYTES = 1_048_576
export const LOGO_MAX_PIXELS = 2000
/** Longest side of the stored, re-encoded logo. */
export const LOGO_STORED_MAX_PX = 600

export const DEFAULT_BRAND_COLOR = "#374151"

export const brandColorSchema = z.string().regex(/^#[0-9A-Fa-f]{6}$/, "must be #RRGGBB")

export const brandingSchema = z.object({
  organisation_id: z.uuid(),
  logo_storage_path: z.string().nullable().default(null),
  logo_width_px: z.number().int().nullable().default(null),
  logo_height_px: z.number().int().nullable().default(null),
  brand_color: z.string().default(DEFAULT_BRAND_COLOR),
  default_template: z.string().default("classic"),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
})

export type Branding = z.infer<typeof brandingSchema>

/** The logo itself is uploaded through its own endpoint; this only sets colour and template. */
export const updateBrandingFields = {
  brand_color: brandColorSchema.optional(),
  default_template: invoiceTemplateSchema.optional(),
}

/** A patch that changes nothing is a client bug (T542); refined schemas cannot be `.extend`ed, so
 * the sync patch builds on `updateBrandingFields` and applies the same check. */
export const hasBrandingChange = (value: { brand_color?: unknown; default_template?: unknown }) =>
  value.brand_color !== undefined || value.default_template !== undefined

export const updateBrandingRequestSchema = z
  .strictObject(updateBrandingFields)
  .refine(hasBrandingChange, { message: "Nothing to update" })

export type UpdateBrandingRequest = z.input<typeof updateBrandingRequestSchema>

/** Copy of the look frozen on an invoice at Send / Charge & issue (R2.5). */
export const brandSnapshotSchema = z.object({
  template: z.string().default("classic"),
  brand_color: z.string().nullable().default(null),
  logo_data_uri: z.string().nullable().default(null),
})

export type BrandSnapshot = z.infer<typeof brandSnapshotSchema>
